import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useWorldStore, ZOOM } from '../state/worldStore';
import { ROOMS } from '../rooms';
import { AUTH_TOKEN, edgeInset } from '../config';
import useAvatarVoiceChat from '../hooks/useAvatarVoiceChat';
import { useAvatarChat } from '../hooks/useAvatarChat';
import { useRideGate } from '../hooks/useRideGate';
import { api } from '../services/api';
import { INTRO_RIDE } from '../worldRide';
import { cityEngage, cityRelease, cityReply, citySocketId, cityTalk, onWorldConnect, serverNow } from '../services/worldSocket';
import { ChatAcceptance, useChatAcceptance } from './ChatAcceptance';
import { useStackSlot, DOCK_MAX_H } from './hudStack';
import { useSceneChat, useNarrow, sceneWrap, SceneHead, useEscToLeave, useChatSpeaking } from './SceneChat';

// AvatarChatDock — talking with an artist you tapped (members).
//
// TEXT FIRST, ONE TAP (Khari, 2026-10-29: *"text should actually be the
// default… Right now, to talk, the user has to click the artist and then press
// a separate button to talk. Then the loading takes longer than usual, while
// the loading icon is on top of the screen not near the input, so it is easily
// missed."*). Tapping an artist now opens the conversation with the box ready
// to type: the same thread, tools and memory as the website (useAvatarChat →
// /api/chat/message), and out in the city the same brief voice gets (where they
// are, that it is public, when they have to go). Voice is the mic in the same
// row: it opens the call only when pressed (a call builds its own agent on the
// server, which is the wait), and every status — typing, connecting,
// listening — sits right above the input, where the eyes already are.
//
// The old lone mic (`QuickMic`) remains only for the street-resident test path
// (?resident=1&city=model), where nobody was tapped. Must live OUTSIDE the Canvas.

const RESIDENT = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('resident') === '1'
  && new URLSearchParams(window.location.search).get('city') === 'model';
const REBEL = { avatarId: 26, name: 'Rebel Rhythm' }; // the street resident POC

const safe = (side, base) => `max(${base}px, env(safe-area-inset-${side}))`;

const pill = {
  position: 'fixed', top: `calc(${safe('top', 16)} + 46px)`, left: '50%', transform: 'translateX(-50%)',
  zIndex: 36, maxWidth: 'min(460px, 82vw)', textAlign: 'center', pointerEvents: 'none',
  background: 'rgba(10,8,12,0.82)', border: '1px solid rgba(255,178,94,0.5)', borderRadius: 14,
  padding: '8px 32px 9px 16px', color: '#ffe9c2', font: '14px system-ui, sans-serif',
  backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
};
const closeBtn = {
  position: 'absolute', top: 4, right: 6, pointerEvents: 'auto',
  background: 'transparent', color: '#ffd27d', border: 'none',
  font: '600 17px system-ui', cursor: 'pointer', lineHeight: 1, padding: 4,
};
const nameLine = { fontSize: 10.5, letterSpacing: 1.2, textTransform: 'uppercase', opacity: 0.7, marginBottom: 2 };

// Matches the HUD's music-toggle chrome: dark translucent, thin amber border,
// muted amber icon. State reads through the BORDER + a faint ring, never a
// bright fill — listening tints toward the neon red, busy states just dim.
const micStyle = (state) => {
  const busy = state === 'thinking' || state === 'connecting';
  const live = state === 'listening';
  return {
    position: 'fixed', left: '50%', bottom: `calc(${safe('bottom', 20)} + var(--hud-player-h, 0px))`, transform: 'translateX(-50%)',
    zIndex: 37, width: 54, height: 54, borderRadius: '50%', display: 'grid', placeItems: 'center',
    touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none',
    pointerEvents: busy ? 'none' : 'auto', cursor: busy ? 'default' : 'pointer',
    background: 'rgba(20,12,6,0.65)',
    border: `1px solid ${live ? 'rgba(255,94,120,0.7)' : 'rgba(255,178,94,0.35)'}`,
    boxShadow: live ? '0 0 0 4px rgba(255,94,120,0.12)' : 'none',
    backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
    opacity: busy ? 0.55 : 1,
    transition: 'border-color .15s ease, box-shadow .15s ease, opacity .15s ease',
  };
};
const STATUS = { connecting: 'Connecting…', listening: 'Listening…', thinking: 'Thinking…', speaking: 'Speaking — talk over them to cut in' };

// Monochrome mic glyph (amber, or neon-red while listening) — no bright emoji.
function MicIcon({ color }) {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <line x1="12" y1="18" x2="12" y2="21" />
      <line x1="8.5" y1="21" x2="15.5" y2="21" />
    </svg>
  );
}

function QuickMic({ target, explicit }) {
  const closeChat = useWorldStore((s) => s.closeChat);
  // OUT IN THE CITY (city/, 2026-10-23): the artist stopped for you. Their own
  // session knows where they are and when they have to go, and what they say
  // back is shared with the people standing near them — their side only.
  const city = target.city || null;
  const onReply = useCallback((text) => { if (city) cityReply(target.avatarId, text); }, [city, target.avatarId]);
  const first = target.name?.split(' ')[0] || 'them';
  const { phase, caption, error, active, toggleTalk, end } = useAvatarVoiceChat({
    avatarId: target.avatarId, surface: city ? 'city' : null, mustGoAt: city?.mustGoAt || null, nextActivity: city?.nextActivity || null, onReply,
    ...(city ? cityTalk(target.avatarId, first) : {}),
  });
  // Walking away (closing, or tapping someone else) lets them get on with their day.
  useEffect(() => () => { if (city) cityRelease(target.avatarId); }, [target.avatarId]); // eslint-disable-line react-hooks/exhaustive-deps

  const hangUp = () => { end(); if (city) cityRelease(target.avatarId); if (explicit) closeChat(); };
  // The acceptance comes first, in the chat's own place (ChatAcceptance).
  const terms = useChatAcceptance();
  if (terms.needed) {
    return (
      <div style={{ position: 'fixed', left: '50%', bottom: `calc(${safe('bottom', 18)} + var(--hud-player-h, 0px))`, transform: 'translateX(-50%)', zIndex: 37 }}>
        <ChatAcceptance checking={terms.checking} onAccept={terms.accept} onClose={explicit ? hangUp : null} />
      </div>
    );
  }

  // Caption only when something's happening — idle, it's just the lone mic.
  const showPill = !!error || !!caption || phase !== 'idle';
  const line = error || caption || STATUS[phase] || '';

  return (
    <>
      {showPill && (
        <div style={pill}>
          {(explicit || active) && <button style={closeBtn} onClick={hangUp} aria-label="End conversation">×</button>}
          <div style={nameLine}>{target.name}</div>
          <div style={{ color: error ? '#ff9a9a' : '#ffe9c2', fontSize: 14.5, lineHeight: 1.35 }}>{line}</div>
        </div>
      )}

      <button
        style={micStyle(phase)}
        onClick={toggleTalk}
        title={active ? `End the conversation with ${first}` : `Talk to ${first}`}
        aria-label={active ? `End the conversation with ${first}` : `Talk to ${first}`}
        aria-pressed={active}
      >
        <MicIcon color={phase === 'listening' ? '#ff8a9c' : '#ffd27d'} />
      </button>
    </>
  );
}

// ── THE CONVERSATION PANEL ───────────────────────────────────────────────────
const panelWrap = {
  position: 'fixed', right: edgeInset(16, 'right'), bottom: `calc(${safe('bottom', 16)} + var(--hud-player-h, 0px))`,
  zIndex: 70, width: 'min(380px, calc(100vw - 32px))', maxHeight: DOCK_MAX_H(540),
  display: 'flex', flexDirection: 'column', pointerEvents: 'auto', fontFamily: 'system-ui, sans-serif',
  background: 'rgba(16,10,6,0.94)', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 16,
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', overflow: 'hidden',
  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
};
const panelHead = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid rgba(255,178,94,0.2)' };
const panelInput = { flex: 1, minWidth: 0, padding: '11px 12px', borderRadius: 10, border: '1px solid rgba(255,178,94,0.3)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 15, fontFamily: 'inherit', outline: 'none', display: 'block', resize: 'none', lineHeight: 1.35, boxSizing: 'border-box' };

/* HOW MUCH ONE MESSAGE MAY HOLD (2026-11-03, was 2,000). Khari: *"I was unable
   to paste all of that for Lyra, which makes me think users will experience the
   same with lyrics."* His 3,254-character page was cut here and had to go in
   two texts. ⚠ The server's limit matches (API routes/chat.js MESSAGE_MAX, and
   the website's config/chat): raise them together. */
const MESSAGE_MAX = 10000;
const BOX_ROWS = 5;

/* ⚠ A TEXTAREA, NOT AN INPUT: a single-line input turns a paste's line breaks
   into spaces, so a pasted song arrived as one run-on line and the server could
   not read it as lyrics (ChatService.findPastedLyrics goes by short lines). One
   row that grows with what is in it, up to BOX_ROWS, then scrolls; Enter sends
   and Shift+Enter is a new line, as the input behaved. */
function useGrowingBox(ref, value) {
  useLayoutEffect(() => {
    const t = ref.current;
    if (!t) return;
    t.style.height = 'auto';
    const cs = getComputedStyle(t);
    const px = (v) => parseFloat(v) || 0;
    const line = px(cs.lineHeight) || px(cs.fontSize) * 1.35;
    const edge = px(cs.borderTopWidth) + px(cs.borderBottomWidth);
    const cap = line * BOX_ROWS + px(cs.paddingTop) + px(cs.paddingBottom) + edge;
    const want = t.scrollHeight + edge;
    t.style.height = `${Math.min(want, cap)}px`;
    t.style.overflowY = want > cap ? 'auto' : 'hidden';
  }, [ref, value]);
}
const roundBtn = (on, live) => ({
  flex: '0 0 auto', width: 42, height: 42, borderRadius: '50%', display: 'grid', placeItems: 'center', cursor: 'pointer',
  background: live ? 'rgba(255,94,120,0.18)' : 'rgba(20,12,6,0.65)',
  border: `1px solid ${live ? 'rgba(255,94,120,0.75)' : on ? 'rgba(255,178,94,0.7)' : 'rgba(255,178,94,0.35)'}`,
  boxShadow: live ? '0 0 0 4px rgba(255,94,120,0.12)' : 'none', transition: 'border-color .15s ease, box-shadow .15s ease',
});
const sendBtn = (ready) => ({
  flex: '0 0 auto', padding: '0 15px', height: 42, borderRadius: 10, border: 'none', cursor: ready ? 'pointer' : 'default',
  font: '700 15px system-ui', background: ready ? '#ffb25e' : 'rgba(255,255,255,0.12)', color: '#1a0f00',
});
const bubble = (mine) => ({
  padding: '8px 12px', borderRadius: 14, fontSize: 14, lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  background: mine ? 'linear-gradient(180deg,#ffb25e,#ff8a4c)' : 'rgba(255,255,255,0.08)',
  color: mine ? '#1a0f00' : '#ffe9c4', border: mine ? 'none' : '1px solid rgba(255,178,94,0.2)',
});
const chip = { cursor: 'pointer', padding: '6px 11px', borderRadius: 999, border: '1px solid rgba(255,178,94,0.45)', background: 'rgba(255,178,94,0.08)', color: '#ffd27d', font: '600 12.5px system-ui' };
const HOLD_RENEW_MS = 2 * 60000; // the city holds an artist 10 min at a time (API CityLifeService)
// A voice DELIVERY TAG at the start of a line ("[soft] I'm on the bench…") is
// direction for the voice, not words to read: the API strips new ones
// (ChatService), this cleans what is already in the history. Same narrow shape:
// one plain word or a pause, never a song section, never mid-line.
const DELIVERY_TAG = /^[ \t]*\[(?!(?:verse|chorus|pre[-\s]?chorus|bridge|hook|intro|outro|refrain|interlude|breakdown|drop|tag)\b)(?:[A-Za-z]+|(?:long|short)[ \t]pause)\][ \t]+(?=\S)/gim;
const spoken = (text) => String(text || '').replace(DELIVERY_TAG, '');
const finePointer = () => { try { return window.matchMedia('(pointer: fine)').matches; } catch { return false; } };

function Dots() {
  return (
    <span aria-hidden="true" style={{ display: 'inline-flex', gap: 3, marginRight: 7, verticalAlign: 'middle' }}>
      {[0, 1, 2].map((i) => <span key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: '#ffd27d', animation: `acdDot 1s ${i * 0.15}s ease-in-out infinite` }} />)}
    </span>
  );
}

function ChatPanel({ target }) {
  const closeChat = useWorldStore((s) => s.closeChat);
  const city = target.city || null;
  const inCity = !!city;
  const first = target.name?.split(' ')[0] || 'them';

  // TEXT — the website's thread. Out in the city, every send says so.
  // Every chat here is held in the World: their body can answer (do_move, API city/moveBook).
  const extra = useCallback(() => ({ world: true, worldSocketId: citySocketId(), ...(inCity ? { surface: 'city', citySocketId: citySocketId() } : {}) }), [inCity]);
  const chat = useAvatarChat({ avatarId: target.avatarId, extra });
  // Whether you have asked something since this panel opened: the preset
  // questions stand until then. Not "ever" — the panel opens your ONE thread with
  // them (the website's too), so anyone who had talked to Lyra before never saw
  // a chip (Khari, 2026-11-01: "I am still not seeing … lyra's question chips").
  // The panel is keyed by who it is, so this starts false for each person.
  const [asked, setAsked] = useState(false);

  // VOICE — the same agent as before, opened only from the mic. The city's
  // engage runs as the call starts; ending a CALL does not release the artist
  // (the conversation is still open in text), closing the PANEL does.
  const onReply = useCallback((text) => { if (inCity) cityReply(target.avatarId, text); }, [inCity, target.avatarId]);
  const engage = inCity ? cityTalk(target.avatarId, first).engage : null;
  const voice = useAvatarVoiceChat({
    avatarId: target.avatarId, surface: inCity ? 'city' : null,
    mustGoAt: city?.mustGoAt || null, nextActivity: city?.nextActivity || null, onReply, engage,
  });

  // Walking away (closing, or tapping someone else) lets them get on with their day.
  useEffect(() => () => { if (inCity) cityRelease(target.avatarId); }, [target.avatarId]); // eslint-disable-line react-hooks/exhaustive-deps
  // TYPING KEEPS THEM STOPPED TOO: the voice call renews the city's hold, and
  // now an open text conversation does (every 2 min, and after a reconnect).
  useEffect(() => {
    if (!inCity) return undefined;
    const t = setInterval(() => { cityEngage(target.avatarId); }, HOLD_RENEW_MS);
    const off = onWorldConnect(() => { cityEngage(target.avatarId); });
    return () => { clearInterval(t); try { off?.(); } catch { /* gone */ } };
  }, [inCity, target.avatarId]);
  // Somewhere to be: past `mustGoAt` they are on their way (checked gently).
  const [gone, setGone] = useState(false);
  useEffect(() => {
    if (!city?.mustGoAt) return undefined;
    const check = () => setGone(serverNow() >= Number(city.mustGoAt));
    check();
    const t = setInterval(check, 5000);
    return () => clearInterval(t);
  }, [city?.mustGoAt]);

  // What was said on a call lands on the thread: show it once the call ends.
  const wasActive = useRef(false);
  useEffect(() => {
    if (voice.active) { wasActive.current = true; return; }
    if (wasActive.current) { wasActive.current = false; chat.reload(); }
  }, [voice.active]); // eslint-disable-line react-hooks/exhaustive-deps

  const slot = useStackSlot();
  const listRef = useRef(null);
  const inputRef = useRef(null);
  useGrowingBox(inputRef, chat.input);
  useEffect(() => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; }, [chat.messages, chat.sending, voice.caption]);
  // Ready to type the moment it opens (not on touch: a keyboard would cover the street).
  useEffect(() => { if (finePointer()) inputRef.current?.focus(); }, [target.avatarId]);

  const terms = useChatAcceptance();
  const close = () => { try { voice.end(); } catch { /* noop */ } closeChat(); };
  // A CHAT SCENE (components/SceneChat): laid over the shot once the camera has
  // them (ChatSceneCamera), instead of the corner panel. Esc leaves; their face
  // moves while their voice plays.
  const sceneOn = useSceneChat((st) => st.on) && !!target.scene;
  const narrow = useNarrow();
  useEscToLeave(sceneOn, close);
  const lastReply = [...(chat.messages || [])].reverse().find((m) => m?.role === 'assistant' && (m.content || '').trim())?.content || null;
  useChatSpeaking(target.avatarId, { voice: voice.phase === 'speaking', reply: lastReply });

  // ⚠ BLOCK THE SEND, NEVER THE BOX: the input stays live through every state.
  const callOn = voice.active || voice.phase === 'connecting';
  const blocked = gone ? `${first} had somewhere to be.` : callOn ? 'On a call — hang up to type.' : '';
  const canSend = !!chat.input.trim() && !chat.sending && !blocked;
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSend) return;
    setAsked(true);
    const reply = await chat.send();
    if (reply && inCity) cityReply(target.avatarId, reply);
  };

  // ONE status line, right above the input.
  const status = voice.requestingMic ? { busy: true, text: 'Allow the mic to talk…' }
    : voice.phase === 'connecting' ? { busy: true, text: `Connecting to ${first}…` }
    : voice.phase === 'listening' ? { live: true, text: 'Listening — just talk' }
    : voice.phase === 'speaking' ? { text: `${first} is speaking — talk over them to cut in` }
    : voice.phase === 'thinking' ? { busy: true, text: `${first} is thinking…` }
    : voice.phase === 'error' ? { err: true, text: voice.error }
    : chat.opening ? { busy: true, text: 'Opening your conversation…' }
    : chat.note ? { err: true, text: chat.note }
    : blocked ? { text: blocked }
    : null;

  const shown = (chat.messages || []).filter((m) => m && (m.role === 'user' || m.role === 'assistant') && (m.content || '').trim()).slice(-30);

  return (
    <div ref={sceneOn ? undefined : slot} style={sceneOn ? sceneWrap(narrow) : panelWrap} role="dialog" aria-label={`Conversation with ${target.name}`}>
      {sceneOn ? <SceneHead name={target.name} sub={target.scene.sub} onClose={close} narrow={narrow} /> : (
        <div style={panelHead}>
          <span style={{ color: '#ffd27d', fontWeight: 700, fontSize: 15 }}>{target.name}</span>
          <button style={{ ...closeBtn, position: 'static', fontSize: 20 }} onClick={close} aria-label="Close conversation">×</button>
        </div>
      )}

      {terms.needed ? (
        <div style={{ padding: 10 }}>
          <ChatAcceptance checking={terms.checking} onAccept={terms.accept} onClose={close} style={{ width: '100%', border: 'none', boxShadow: 'none', background: 'transparent', padding: 6 }} />
        </div>
      ) : (
        <>
          <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 120 }}>
            {!shown.length && !chat.opening && (
              <div style={{ color: 'rgba(255,233,196,0.55)', textAlign: 'center', fontSize: 13, marginTop: 18 }}>Say hi to {first}</div>
            )}
            {shown.map((m, i) => (
              <div key={m.id || `l${i}`} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '84%' }}>
                <div style={bubble(m.role === 'user')}>{m.role === 'assistant' ? spoken(m.content) : m.content}</div>
              </div>
            ))}
            {chat.sending && (
              <div style={{ alignSelf: 'flex-start', color: 'rgba(255,233,196,0.7)', fontSize: 13, padding: '4px 2px' }}><Dots />{first} is typing…</div>
            )}
            {callOn && voice.caption && (
              <div style={{ alignSelf: 'flex-start', maxWidth: '84%' }}><div style={{ ...bubble(false), fontStyle: 'italic' }}>{voice.caption}</div></div>
            )}
          </div>

          {/* PRESET QUESTIONS (a front desk's, rooms.js `frontDesk.questions`):
              until you have asked something yourself, in this panel. */}
          {Array.isArray(target.questions) && target.questions.length > 0 && !chat.sending && !chat.opening && !blocked
            && !chat.choices?.length && !asked && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 14px 8px' }} aria-label="Suggested questions">
              {target.questions.map((q) => (
                <button key={q} type="button" style={chip} onClick={async () => {
                  setAsked(true);
                  const reply = await chat.send(q);
                  if (reply && inCity) cityReply(target.avatarId, reply);
                }}>{q}</button>
              ))}
            </div>
          )}

          {chat.choices?.length > 0 && !chat.sending && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 14px 8px' }}>
              {chat.choices.map((c, i) => (
                <button key={i} style={chip} onClick={() => chat.pick(c.value ?? c.label ?? c)}>{c.label ?? c.value ?? String(c)}</button>
              ))}
            </div>
          )}

          {status && (
            <div role="status" style={{ padding: '6px 14px', fontSize: 12.5, borderTop: '1px solid rgba(255,178,94,0.12)',
              color: status.err ? '#ff9a9a' : status.live ? '#ff8a9c' : 'rgba(255,233,196,0.8)' }}>
              {status.busy && <Dots />}{status.text}
            </div>
          )}

          <form onSubmit={submit} style={{ display: 'flex', gap: 8, padding: 10, borderTop: '1px solid rgba(255,178,94,0.2)', alignItems: 'flex-end' }}>
            <textarea
              ref={inputRef}
              rows={1}
              value={chat.input}
              onChange={(e) => chat.setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
                e.preventDefault();
                submit();
              }}
              placeholder={`Message ${first}…`}
              maxLength={MESSAGE_MAX}
              style={panelInput}
              aria-label={`Message ${first}`}
            />
            <button type="button" style={roundBtn(callOn, voice.phase === 'listening')} onClick={voice.toggleTalk}
              aria-pressed={voice.active} aria-label={voice.active ? `Hang up with ${first}` : `Talk to ${first} out loud`}
              title={voice.active ? 'Hang up' : 'Talk out loud'}>
              <MicIcon color={voice.phase === 'listening' ? '#ff8a9c' : '#ffd27d'} />
            </button>
            <button type="submit" style={sendBtn(canSend)} disabled={!canSend} aria-label="Send">↑</button>
          </form>
        </>
      )}
      <style>{'@keyframes acdDot { 0%,80%,100% { opacity: .25; transform: translateY(0) } 40% { opacity: 1; transform: translateY(-2px) } }'}</style>
    </div>
  );
}

export default function AvatarChatDock() {
  const chatAvatar = useWorldStore((s) => s.chatAvatar);
  const zoom = useWorldStore((s) => s.zoom);
  const interior = useWorldStore((s) => s.interior);

  // Who the mic talks to: an explicitly-clicked avatar wins; otherwise the
  // street resident (when present). Interiors keep their own video dock
  // (LiveChatDock), so we don't double up there unless one was clicked.
  const setMicVisible = useWorldStore((s) => s.setMicVisible);
  const roomHasVideoChat = zoom === ZOOM.INTERIOR && !!ROOMS[interior]?.liveChat;
  const streetDefault = zoom === ZOOM.STREET && RESIDENT ? REBEL : null;
  const target = chatAvatar || (roomHasVideoChat ? null : streetDefault);
  const show = !!target && !!AUTH_TOKEN;
  // Let the movement controls know whether the center mic is up (so they raise
  // above it only when it's actually there, and drop back when it's gone). The
  // panel sits at the side, clear of them; only the test path's mic is centred.
  const centreMic = show && !chatAvatar;
  useEffect(() => { setMicVisible(centreMic); return () => setMicVisible(false); }, [centreMic, setMicVisible]);

  // TAPPING SPOR1 *IS* THE PICKUP — the member half of what GuestChatDock
  // already does. Boarding belongs in the conversation, not on a magic spot
  // near the car; his scene opens with "It's open — get in," so the chat you
  // asked for IS the pickup. The kerb card stays for walk-ups.
  //
  // The one thing guests can hardcode and members cannot: WHICH ride. A guest
  // is a stranger by definition, so the interview is always right. A member
  // may be someone Spor1 already knows, and that is the server's call
  // (GET /api/dialogue/ride → the homecoming variant), never a client guess.
  // If the call fails the interview stands — meeting someone twice is a
  // smaller wrong than greeting a returning artist as a stranger... which is
  // why we ASK, and only fall back when we cannot.
  //
  // And it waits for the story to have STARTED: `rideOpen` is false until
  // Lyra's call has been answered (useRideGate — the same rule the beacon and
  // the kerb card use). Spor1 being the first person you must talk to makes
  // him the easiest thing to trigger by accident on spawn.
  //
  // Hooks must stay ABOVE the early return below (a hook under it crashed the
  // guest dock: "Rendered more hooks than during the previous render").
  const myAvatarId = useWorldStore((s) => s.myAvatarId);
  const { rideOpen } = useRideGate();
  const boardedFor = useRef(null);

  useEffect(() => {
    const id = chatAvatar?.avatarId;
    if (!AUTH_TOKEN || !id || !myAvatarId || !rideOpen) return undefined;
    if (!/spor\s*1/i.test(chatAvatar.name || '')) return undefined;
    // ⚠ THE PICKUP IS ON THE STREET (2026-10-31). Spor1 is a city resident too:
    // tapping him at a Jahva House table started the car ride instead of the
    // conversation (Khari: "I am unable to speak with the artists at the
    // tables"). Indoors he is just someone to talk to.
    if (useWorldStore.getState().interior) return undefined;
    if (boardedFor.current === id) return undefined;         // one board per tap
    if (useWorldStore.getState().ride) return undefined;      // already in the car
    boardedFor.current = id;

    let alive = true;
    (async () => {
      let key = 'act2_ride_spor1';
      let title = "Spor1's ride";
      try {
        const d = await api.dialogueRide(myAvatarId);
        if (d?.scene) { key = d.scene; title = d.title || title; }
      } catch { /* interview stands */ }
      if (!alive) return;
      const st = useWorldStore.getState();
      if (st.ride) return;
      st.closeChat();
      st.openScene({ key, title, quest: 'come_up_the_ride', room: null });
      st.startRide({ sceneKey: key, pace: INTRO_RIDE.pace });
    })();
    return () => { alive = false; };
  }, [chatAvatar?.avatarId, myAvatarId, rideOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!show) return null;

  // Tapped someone: the conversation panel. Nobody tapped (test path): the lone mic.
  return chatAvatar
    ? <ChatPanel key={target.avatarId} target={target} />
    : <QuickMic key={target.avatarId} target={target} explicit={false} />;
}
